import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useProfile } from "src/auth/Auth";
import Permissions from "src/common_constants/Permission";
import useFetch from "src/hooks/useFetch";
import useHandleSubmit from "src/hooks/useHandleSubmit";
import EditPageWrapper from "src/shared_components/EditPageWrapper";
import PermissionRestrictedComponent from "src/shared_components/PermissionRestrictedComponent";
import CheckBoxField from "src/shared_components/FormElements/CheckBoxField";
import { getTaxes, getTaxMappings, saveTaxMappings } from "src/apis/TaxApis";
import * as yup from "yup";
import AddClearButtons from "src/shared_components/buttons/AddClearButtons";
import { Height } from "@mui/icons-material";

interface TaxMappingFormProps {}

interface TaxMappingData {
  taxMappings: Array<{
    id: string;
    name: string;
    type: "addon" | "rateplan";
    itemGroup?: string;
    taxes: Array<{
      taxId: number;
      taxName: string;
      isSelected: boolean;
    }>;
  }>;
}

const TaxMappingForm: React.FC<TaxMappingFormProps> = ({}) => {
  const { clientId } = useProfile();
  const { t } = useTranslation();
  const [taxes, setTaxes] = useState<any[]>([]);
  const [items, setItems] = useState<any[]>([]);
  const [existingMappings, setExistingMappings] = useState<any[]>([]);
  const [mappingData, setMappingData] = useState<TaxMappingData>({
    taxMappings: [],
  });
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [refresh, setRefersh] = useState<boolean>(false);
  const [originalMappings, setOriginalMappings] = useState<any[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);

  const validationSchema = yup.object().shape({
    taxMappings: yup
      .array()
      .of(
        yup.object().shape({
          id: yup.string().required(),
          itemGroup: yup.string().optional(),
          name: yup.string().required(),
          type: yup.string().oneOf(["addon", "rateplan"]).required(),
          taxes: yup
            .array()
            .of(
              yup.object().shape({
                taxId: yup.number().required(),
                taxName: yup.string().required(),
                isSelected: yup.boolean().required(),
              })
            )
            .required(),
        })
      )
      .required(),
  });

  const methods = useForm<TaxMappingData>({
    mode: "onChange",
    resolver: yupResolver<TaxMappingData>(validationSchema),
    defaultValues: {
      taxMappings: [],
    },
  });

  const {
    control,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = methods;

  const [__, mappingsLoading] = useFetch(
    () => getTaxMappings({ clientId }),
    [clientId, refresh],
    true,
    (data) => {
      setItems(data.items || []);
      setExistingMappings(data.mappings || []);
      setTaxes(data.taxes || []);
    }
  );

  useEffect(() => {
    if (items.length > 0 && taxes.length > 0) {
      const mappings: TaxMappingData["taxMappings"] = [];

      const addOns = items.filter((item) => item.referenceType === "AddOn");
      const ratePlans = items.filter(
        (item) => item.referenceType === "AssetRateName"
      );

      addOns.forEach((item) => {
        mappings.push({
          id: `${item.referenceType}_${item.referenceId}`,
          name: item.name,
          type: "addon",
          itemGroup: item.itemGroup,
          taxes: taxes.map((tax) => {
            const isMapped = existingMappings.some(
              (mapping) =>
                mapping.taxId === tax.taxId &&
                mapping.referenceId === item.referenceId &&
                mapping.referenceType === item.referenceType
            );
            return {
              taxId: tax.taxId,
              taxName: tax.name,
              isSelected: isMapped,
            };
          }),
        });
      });

      ratePlans.forEach((item) => {
        mappings.push({
          id: `${item.referenceType}_${item.referenceId}`,
          name: item.name,
          type: "rateplan",
          itemGroup: item.itemGroup,
          taxes: taxes.map((tax) => {
            const isMapped = existingMappings.some(
              (mapping) =>
                mapping.taxId === tax.taxId &&
                mapping.referenceId === item.referenceId &&
                mapping.referenceType === item.referenceType
            );
            return {
              taxId: tax.taxId,
              taxName: tax.name,
              isSelected: isMapped,
            };
          }),
        });
      });

      setMappingData({ taxMappings: mappings });
      setValue("taxMappings", mappings);

      const originalMappingsArray = existingMappings.map((mapping) => ({
        taxId: mapping.taxId,
        referenceId: mapping.referenceId,
        referenceType: mapping.referenceType,
      }));
      setOriginalMappings(originalMappingsArray);
    }
  }, [items, taxes, existingMappings, setValue]);

  const hasChanges = () => {
    const formData = getValues();
    const currentMappings: any[] = [];

    formData.taxMappings.forEach((mapping) => {
      mapping.taxes.forEach((tax) => {
        if (tax.isSelected) {
          currentMappings.push({
            taxId: tax.taxId,
            referenceId: parseInt(mapping.id.split("_")[1]),
            referenceType: mapping.type === "addon" ? "AddOn" : "AssetRateName",
          });
        }
      });
    });

    if (currentMappings.length !== originalMappings.length) {
      return true;
    }

    return !currentMappings.every((currentMapping) =>
      originalMappings.some(
        (originalMapping) =>
          originalMapping.taxId === currentMapping.taxId &&
          originalMapping.referenceId === currentMapping.referenceId &&
          originalMapping.referenceType === currentMapping.referenceType
      )
    );
  };

  const onSubmit = () => {
    // Check if there are any changes
    if (!hasChanges()) {
      setSuccessMessage("No changes to save");
      setTimeout(() => setSuccessMessage(null), 2000);
      return;
    }

    const formData = getValues();

    const mappingsToSave: any[] = [];

    formData.taxMappings.forEach((mapping) => {
      mapping.taxes.forEach((tax) => {
        if (tax.isSelected) {
          mappingsToSave.push({
            taxId: tax.taxId,
            referenceId: parseInt(mapping.id.split("_")[1]),
            referenceType: mapping.type === "addon" ? "AddOn" : "AssetRateName",
          });
        }
      });
    });

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    saveTaxMappings(mappingsToSave)
      .then(() => {
        setSuccessMessage("Tax mappings saved successfully!");
        setError(null);
        setSaving(false);

        setOriginalMappings(mappingsToSave);
        setHasUnsavedChanges(false);

        setTimeout(() => setSuccessMessage(null), 3000);
        setRefersh((prev) => !prev);
      })
      .catch((error) => {
        setError("Failed to save tax mappings");
        setSuccessMessage(null);
        setSaving(false);
      });
  };

  const loading = mappingsLoading;
  return (
    <FormProvider {...methods}>
      <EditPageWrapper
        id={0}
        loading={loading}
        onSubmit={onSubmit}
        editTitle=""
        addTitle=""
        createOrModifyPermission={Permissions.AssetRateCreateModify}
        isSubmitBtn={false}
      >
        <Box sx={{ width: "100%", overflow: "auto" }}>
          {mappingData.taxMappings.length === 0 && !loading ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body1" color="text.secondary">
                {t("No data available")}
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
              <Table stickyHeader>
                <TableHead sx={{ height: "40px" }}>
                  <TableRow>
                    <TableCell
                      sx={{
                        backgroundColor: "#e3f2fd",
                        color: "text.primary",
                        fontWeight: "bold",
                        minWidth: 200,
                        position: "sticky",
                        left: 0,
                        zIndex: 5,
                      }}
                    >
                      {t("Item Name")}
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "#e3f2fd",
                        color: "text.primary",
                        fontWeight: "bold",
                        minWidth: 120,
                        position: "sticky",
                        left: 200,
                        zIndex: 5,
                      }}
                    >
                      {t("Item Type")}
                    </TableCell>
                    {taxes.map((tax) => (
                      <TableCell
                        key={tax.taxId}
                        align="center"
                        sx={{
                          backgroundColor: "#e3f2fd",
                          color: "text.primary",
                          fontWeight: "bold",
                          minWidth: 120,
                        }}
                      >
                        {tax.name}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mappingData.taxMappings.map((mapping, mappingIndex) => (
                    <TableRow key={mapping.id}>
                      <TableCell
                        sx={{
                          backgroundColor: "#e8f5e8",
                          color: "text.primary",
                          fontWeight: "bold",
                          position: "sticky",
                          left: 0,
                          zIndex: 1,
                        }}
                      >
                        <Typography variant="body2" fontWeight="bold">
                          {mapping.name}
                        </Typography>
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: "#e8f5e8",
                          color: "text.primary",
                          fontWeight: "bold",
                          position: "sticky",
                          left: 200,
                          zIndex: 1,
                        }}
                      >
                        <Typography
                          variant="caption"
                          // sx={{
                          //   backgroundColor: mapping.type === 'addon' ? 'info.main' : 'warning.main',
                          //   color: 'white',
                          //   px: 1,
                          //   py: 0.5,
                          //   borderRadius: 1,
                          //   fontSize: '0.7rem',
                          //   fontWeight: 'bold'
                          // }}
                        >
                          {mapping.itemGroup || "-"}
                        </Typography>
                      </TableCell>
                      {mapping.taxes.map((tax, taxIndex) => (
                        <TableCell key={tax.taxId} align="center">
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <Checkbox
                              checked={tax.isSelected}
                              onChange={(e) => {
                                const currentMappings =
                                  getValues("taxMappings");
                                const updatedMappings = [...currentMappings];
                                updatedMappings[mappingIndex].taxes[
                                  taxIndex
                                ].isSelected = e.target.checked;

                                setValue("taxMappings", updatedMappings);
                                setMappingData({
                                  taxMappings: updatedMappings,
                                });

                                // Check for changes after update
                                setTimeout(() => {
                                  setHasUnsavedChanges(hasChanges());
                                }, 100);
                              }}
                              color="primary"
                            />
                          </Box>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <AddClearButtons handleSubmit={handleSubmit(onSubmit)} />
        </Box>
      </EditPageWrapper>
    </FormProvider>
  );
};

export default TaxMappingForm;
